import { useMemo } from "react"
import cn from "classnames"

import { Img, Text } from "ui-kit/components-branded/index"
import { HelpTooltip } from "components"
import { PsychologistProfile } from "modules/ClientAccount/components/Sessions/PsychologistProfile"
import { PaymentInfo } from "modules/ClientAccount/components/Sessions/PaymentInfo"
import { Session, Datetime } from "modules/ClientAccount/components/Sessions/index"
import { parsePhoneNumber } from "utils/phone-number"
import { formatToLong, formatToShort, formatToWeekDays } from "utils/datetime-format-utils"

function Headline({ isOffline }: { isOffline: boolean }) {
    return isOffline ? (
        <Text isAccent>
            <Img className="sessions__emoji" src="person-emoji.png" />
            Личная сессия
        </Text>
    ) : (
        <Text isAccent>
            <Img className="sessions__emoji" src="person-with-laptop-emoji.png" />
            Онлайн-сессия
        </Text>
    )
}

function StartDatetimeChangedTooltip({
    previousStartDatetime,
    startDatetimeChangedDatetime,
}: {
    previousStartDatetime: Datetime
    startDatetimeChangedDatetime: Datetime
}) {
    return (
        <HelpTooltip
            overlay={
                <Text isSecondary className="has-text-dark-60">
                    Предыдущее время: {formatToShort(previousStartDatetime)} <br />
                    Перенесено {formatToShort(startDatetimeChangedDatetime)}
                </Text>
            }
            placement="bottom"
        />
    )
}

function FixedSessionTimeTooltip({ sessionDatetime }: { sessionDatetime: Datetime }) {
    return (
        <HelpTooltip
            overlay={
                <Text isSecondary className="has-text-dark-60">
                    <div className="no-wrap">
                        Закрепленное время: {formatToWeekDays(sessionDatetime)}
                    </div>
                    Сессии будут автоматически назначаться на это время. Если вы хотите выбрать
                    другое время, попросите об этом психолога.
                </Text>
            }
            placement="bottom"
        />
    )
}

function FixedSessionTimeAndStartDatetimeChangedTooltip({
    previousStartDatetime,
    startDatetimeChangedDatetime,
}: {
    previousStartDatetime: Datetime
    startDatetimeChangedDatetime: Datetime
}) {
    return (
        <HelpTooltip
            overlay={
                <>
                    <Text isSecondary className="has-text-dark-60">
                        Предыдущее время: {formatToShort(previousStartDatetime)} <br />
                        Перенесено {formatToShort(startDatetimeChangedDatetime)} <br />
                    </Text>
                    {/* TODO: В следующей задаче sessionDatetime поменять на fixedSessionStartDatetime */}
                    {/* https://app.clickup.com/t/325p2xb */}
                    {/* <hr className="mgt-4 mgb-4 has-bg-dark-20" />
                    <Text isSecondary className="has-text-dark-60">
                        Закрепленное время: {formatToWeekDays(sessionDatetime)} <br />
                        Сессии будут автоматически назначаться на это время. Если вы хотите выбрать
                        другое время, попросите об этом психолога.
                    </Text> */}
                </>
            }
            placement="bottom"
        />
    )
}

function SessionDatetime({
    startDatetime,
    previousStartDatetime,
    startDatetimeChangedDatetime,
    hasFixedSessionTime,
}: {
    startDatetime: Datetime | null
    previousStartDatetime: Datetime | null
    startDatetimeChangedDatetime: Datetime | null
    hasFixedSessionTime: boolean
}) {
    return (
        <div className="is-flex mgt-4 is-nowrap">
            {hasFixedSessionTime && !startDatetimeChangedDatetime && (
                <>
                    <Img className="sessions__emoji-clock" src="lock-emoji.png" />
                    &nbsp;
                </>
            )}
            {startDatetimeChangedDatetime && (
                <>
                    <Img className="sessions__emoji-clock" src="alarm-clock-emoji.png" />
                    &nbsp;
                </>
            )}
            <Text>{startDatetime && formatToLong(startDatetime)}</Text>
            {previousStartDatetime && startDatetimeChangedDatetime && !hasFixedSessionTime && (
                <>
                    &nbsp;
                    <StartDatetimeChangedTooltip
                        previousStartDatetime={previousStartDatetime}
                        startDatetimeChangedDatetime={startDatetimeChangedDatetime}
                    />
                </>
            )}
            {hasFixedSessionTime &&
                startDatetime &&
                !previousStartDatetime &&
                !startDatetimeChangedDatetime && (
                    <>
                        &nbsp;
                        <FixedSessionTimeTooltip sessionDatetime={startDatetime} />
                    </>
                )}
            {hasFixedSessionTime &&
                startDatetime &&
                previousStartDatetime &&
                startDatetimeChangedDatetime && (
                    <>
                        &nbsp;
                        <FixedSessionTimeAndStartDatetimeChangedTooltip
                            previousStartDatetime={previousStartDatetime}
                            startDatetimeChangedDatetime={startDatetimeChangedDatetime}
                        />
                    </>
                )}
        </div>
    )
}

function PhoneNumber({ phone }: { phone: string }) {
    const formatted = useMemo(() => parsePhoneNumber(phone).groupedFormat ?? phone, [phone])
    return (
        <div className="mgt-4">
            <Text>
                Номер для связи:{" "}
                <a href={`tel:${formatted}`} className="sessions__phone">
                    {formatted}
                </a>
            </Text>
        </div>
    )
}

export function ProfileAndInfo({ session }: { session: Session }) {
    const psy = session.psy
    return (
        <div>
            <div className="is-flex align-items-center">
                <div className="sessions__card__half">
                    <Headline isOffline={session.isOffline} />
                    <SessionDatetime
                        startDatetime={session.startDatetime}
                        previousStartDatetime={session.previousStartDatetime}
                        startDatetimeChangedDatetime={session.startDatetimeChangedDatetime}
                        hasFixedSessionTime={session.hasFixedSessionTime}
                    />
                    {!session.sessionEnded && !session.canceled && (
                        <PhoneNumber phone={session.psy.phone_number} />
                    )}
                    {session.paid && (
                        <PaymentInfo
                            price={session.price}
                            forFree={session.forFree}
                            moneyBack={session.isRefundApplied}
                            paid={session.paid}
                            currency={session.currency}
                            canceled={session.canceled}
                        />
                    )}
                </div>
                <PsychologistProfile
                    faceUrl={psy.face_url}
                    hashId={psy.hash_id}
                    name={psy.name}
                    title={psy.title}
                    publicProfileUrl={psy.public_profile_url}
                    className="is-hidden-touch sessions__card__half"
                />
            </div>
            <hr
                className={cn("sessions__card__hr mgt-3 mgb-3", {
                    "is-hidden-desktop":
                        (!session.bookNextSession && session.sessionEnded) ||
                        (!session.bookNextSession && session.canceled),
                })}
            />
            <PsychologistProfile
                faceUrl={psy.face_url}
                hashId={psy.hash_id}
                name={psy.name}
                title={psy.title}
                publicProfileUrl={psy.public_profile_url}
                className="is-hidden-desktop"
            />
        </div>
    )
}
